import { chakra, Box, Stack, Text, useToast } from '@chakra-ui/react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useCallback, useMemo, useState } from 'react';
import { CourseItemType, Item } from './Item';
import type { ResourceCoverageLike } from '../../components/translations/TranslationCoverageBadge';
import {
  courseHasPendingTranslation,
  TRANSLATION_POLL_INTERVAL_MS,
} from '../../components/translations/coverageStatus';
import { Reorder } from 'framer-motion';

const List = chakra(Reorder.Group);

export function UnitList({
  courseId,
  courseItems,
  scheduledUnitIds,
}: {
  courseId: string;
  courseItems: CourseItemType[];
  scheduledUnitIds: Set<string>;
}) {
  const toast = useToast();
  const trpcUtils = trpcAdmin.useUtils();
  const [isDeleteConfirmationAlertOpen, setIsDeleteConfirmationAlertOpen] = useState(false);
  const [selectedCourseItemId, setSelectedCourseItemId] = useState<string | null>(null);
  const [courseItemsState, setCourseItemsState] = useState<any[]>([]);

  // Reuses the coverage card's listByCourse query, so this is a cache hit, not a
  // second round-trip.
  const { data: courseCoverage } = trpcAdmin.contentTranslations.listByCourse.useQuery(
    { courseId },
    {
      refetchInterval: (query) =>
        courseHasPendingTranslation(query.state.data?.units) ? TRANSLATION_POLL_INTERVAL_MS : false,
    },
  );
  const coverageByUnitId = useMemo(() => {
    const map = new Map<string, ResourceCoverageLike[]>();
    for (const unit of courseCoverage?.units ?? []) {
      if (unit.unitOfCompetencyId === null) {
        continue;
      }
      map.set(
        unit.unitOfCompetencyId,
        unit.resources.map((resource) => ({
          currentPublishedVersionId: resource.currentPublishedVersionId,
          rows: resource.rows,
        })),
      );
    }
    return map;
  }, [courseCoverage]);

  useMemo(() => {
    if (courseItems) {
      setCourseItemsState(courseItems.sort((a, b) => a.order - b.order));
    }
  }, [courseItems]);

  const deleteCourseItem = trpcAdmin.courses.deleteCourseItem.useMutation();

  const reorderCourseItems = trpcAdmin.courses.reorderCourseItems.useMutation({
    async onMutate(newOrder) {
      // Cancel outgoing fetches (so they don't overwrite our optimistic update)
      await trpcUtils.courses.byId.cancel();

      // Get the data from the queryCache
      const prevData = trpcUtils.courses.byId.getData();

      // Optimistically update the data with our new order
      trpcUtils.courses.byId.setData(courseId, (prevData: any) => {
        if (!prevData) {
          return;
        }

        const updatedCourseItems = newOrder.courseItemIds.map((courseItemId) =>
          prevData.courseItems.find((courseItem) => courseItem.id === courseItemId),
        );

        return {
          ...prevData,
          courseItems: updatedCourseItems,
        };
      });

      // Return the previous data so we can revert if something goes wrong
      return { prevData };
    },
    onError(_err, _newOrder, ctx) {
      // If the mutation fails, use the context-value from onMutate
      if (ctx) {
        trpcUtils.courses.byId.setData(courseId, ctx.prevData);
      }
    },
    onSettled() {
      void trpcUtils.courses.byId.invalidate();
    },
  });

  const saveReorder = useCallback(() => {
    reorderCourseItems.mutate({
      courseItemIds: courseItemsState.map((courseItem) => courseItem.id),
    });
  }, [courseItemsState, reorderCourseItems]);

  const onClickDeleteCourseItem = useCallback(
    (courseItemId: string) => {
      deleteCourseItem.mutate(
        { courseItemId },
        {
          onSuccess: () => {
            // remove the course item from the courseItems list
            const updatedCourseItems = courseItemsState.filter(
              (courseItem) => courseItem.id !== courseItemId,
            );

            // update the order of the courseItems
            reorderCourseItems.mutate({
              courseItemIds: updatedCourseItems.map((courseItem) => courseItem.id),
            });

            toast({
              title: t`Course item deleted successfully.`,
              status: 'success',
              isClosable: true,
            });
          },
        },
      );
    },
    [courseItemsState, deleteCourseItem, reorderCourseItems, toast],
  );

  return (
    <>
      <List
        aria-label={t`Units of competency`}
        values={courseItemsState}
        onReorder={setCourseItemsState}
        listStyleType="none"
      >
        <Stack spacing="4" width="full">
          {courseItemsState.map((item: CourseItemType) => (
            <Item
              key={item.id}
              item={item}
              courseId={courseId}
              allCourseItems={courseItemsState}
              scheduledUnitIds={scheduledUnitIds}
              setSelectedCourseItemId={setSelectedCourseItemId}
              setIsDeleteConfirmationAlertOpen={setIsDeleteConfirmationAlertOpen}
              saveReorder={saveReorder}
              coverage={
                item.unit_of_competency_id !== null
                  ? (coverageByUnitId.get(item.unit_of_competency_id) ?? [])
                  : undefined
              }
              coverageLanguages={courseCoverage?.supportedLanguages}
            />
          ))}
        </Stack>
      </List>

      <ConfirmationDialog
        isOpen={isDeleteConfirmationAlertOpen}
        onClose={() => setIsDeleteConfirmationAlertOpen(false)}
        title={<Trans>Delete Conversational Flow</Trans>}
        body={
          <Box>
            <Trans>
              You are about to delete this item from the course. Are you sure you want to continue?
            </Trans>
            {selectedCourseItemId ? (
              <Box
                mt={4}
                p={4}
                borderColor="danger.subtle"
                bg="bg.danger-subtle"
                borderRadius="8px"
              >
                <Text fontSize="sm" fontWeight="medium">
                  {
                    courseItemsState?.find((item) => item.id === selectedCourseItemId)
                      ?.unitOfCompetency?.title
                  }
                </Text>
              </Box>
            ) : null}
          </Box>
        }
        onConfirm={() => {
          if (selectedCourseItemId) {
            onClickDeleteCourseItem(selectedCourseItemId);
          }
          setIsDeleteConfirmationAlertOpen(false);
        }}
        confirmLabel={<Trans>Delete</Trans>}
        variant="destructive"
      />
    </>
  );
}
